// src/admin/CouponManagement.js
import React, { useState, useEffect } from 'react';
import {
  collection, getDocs, doc, setDoc, updateDoc, deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  Plus, Edit, Trash2, X, Save, Tag, AlertCircle, CheckCircle,
  Copy, Calendar, Percent, DollarSign, Users, Search
} from 'lucide-react';
import SuccessModal from '../components/SuccessModal';

const CouponManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [copiedCode, setCopiedCode] = useState('');

  const defaultFormData = {
    code: '',
    description: '',
    type: 'percentage', // 'percentage' | 'flat'
    value: '',
    minOrder: '',
    maxDiscount: '',
    usageLimit: '',
    usedCount: 0,
    expiryDate: '',
    active: true,
  };

  const [formData, setFormData] = useState(defaultFormData);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'coupons'));
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setCoupons(data);
    } catch (error) {
      console.error('Error fetching coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, code });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!formData.code || !formData.value) {
      setMessage({ type: 'error', text: 'Code aur Discount Value zaroori hai.' });
      return;
    }

    const couponData = {
      code: formData.code.toUpperCase().trim(),
      description: formData.description || '',
      type: formData.type,
      value: Number(formData.value) || 0,
      minOrder: Number(formData.minOrder) || 0,
      maxDiscount: Number(formData.maxDiscount) || 0,
      usageLimit: Number(formData.usageLimit) || 0,
      usedCount: formData.usedCount || 0,
      expiryDate: formData.expiryDate || '',
      active: formData.active === true,
    };

    try {
      if (editingCoupon) {
        await updateDoc(doc(db, 'coupons', editingCoupon.id), couponData);
        setSuccessMessage('Coupon updated!');
      } else {
        // Check for duplicate code
        const duplicate = coupons.find(c => c.code === couponData.code && c.id !== editingCoupon?.id);
        if (duplicate) {
          setMessage({ type: 'error', text: 'Ye coupon code already exists.' });
          return;
        }

        const newId = Date.now().toString();
        await setDoc(doc(db, 'coupons', newId), {
          ...couponData,
          createdAt: new Date(),
        });
        setSuccessMessage('Coupon created!');
      }
      setShowModal(false);
      setShowSuccess(true);
      fetchCoupons();
    } catch (error) {
      console.error('Save error:', error);
      setMessage({ type: 'error', text: 'Failed to save coupon.' });
    }
  };

  const handleDelete = async (coupon) => {
    if (!window.confirm(`Delete coupon "${coupon.code}"?`)) return;
    try {
      await deleteDoc(doc(db, 'coupons', coupon.id));
      setSuccessMessage('Coupon deleted!');
      setShowSuccess(true);
      fetchCoupons();
    } catch (error) {
      alert('Failed to delete.');
    }
  };

  const toggleActive = async (coupon) => {
    try {
      await updateDoc(doc(db, 'coupons', coupon.id), {
        active: !coupon.active,
      });
      fetchCoupons();
    } catch (error) {
      console.error(error);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(''), 1500);
  };

  const openAddModal = () => {
    setEditingCoupon(null);
    setFormData(defaultFormData);
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  const openEditModal = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code || '',
      description: coupon.description || '',
      type: coupon.type || 'percentage',
      value: coupon.value || '',
      minOrder: coupon.minOrder || '',
      maxDiscount: coupon.maxDiscount || '',
      usageLimit: coupon.usageLimit || '',
      usedCount: coupon.usedCount || 0,
      expiryDate: coupon.expiryDate || '',
      active: coupon.active !== false,
    });
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  const isExpired = (coupon) => {
    if (!coupon.expiryDate) return false;
    return new Date(coupon.expiryDate) < new Date();
  };

  const isLimitReached = (coupon) => {
    if (!coupon.usageLimit) return false;
    return (coupon.usedCount || 0) >= coupon.usageLimit;
  };

  const filteredCoupons = coupons.filter(c =>
    c.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Tag className="text-green-700" size={26} /> Coupons Management
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Discount coupons banaye jo customers checkout par use kar sakein.
          </p>
        </div>
        <button onClick={openAddModal}
          className="bg-green-700 text-white px-5 py-3 rounded-lg hover:bg-green-800 transition flex items-center gap-2 font-semibold shadow-md">
          <Plus size={18} /> Add Coupon
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-md p-4">
        <div className="relative">
          <input type="text" placeholder="Search coupons..."
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border border-gray-300 rounded-lg py-3 px-4 pl-11 focus:outline-none focus:border-green-600 text-sm" />
          <Search className="absolute left-3 top-3.5 text-gray-400" size={18} />
        </div>
      </div>

      {/* Coupons Grid */}
      {loading ? (
        <div className="text-center py-16">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-green-700 border-t-transparent"></div>
          <p className="mt-4 text-gray-500">Loading coupons...</p>
        </div>
      ) : filteredCoupons.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Tag size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No coupons yet. Add your first one!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCoupons.map(coupon => {
            const expired = isExpired(coupon);
            const limitReached = isLimitReached(coupon);
            const status = !coupon.active ? 'INACTIVE'
              : expired ? 'EXPIRED'
              : limitReached ? 'LIMIT REACHED'
              : 'ACTIVE';
            const statusColor = status === 'ACTIVE' ? 'bg-green-500'
              : status === 'INACTIVE' ? 'bg-gray-500'
              : 'bg-red-500';

            return (
              <div key={coupon.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition">
                {/* Top - Code */}
                <div className="p-4 relative" style={{ background: 'linear-gradient(135deg, #16a34a 0%, #14532d 100%)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-full text-white ${statusColor}`}>
                      {status}
                    </span>
                    <button onClick={() => toggleActive(coupon)}
                      className="text-white text-[10px] font-semibold hover:underline">
                      {coupon.active ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/70 text-xs mb-1">Coupon Code</p>
                      <p className="text-white text-2xl font-bold font-mono tracking-widest">{coupon.code}</p>
                    </div>
                    <button onClick={() => copyCode(coupon.code)}
                      className="bg-white/20 backdrop-blur-md text-white p-2 rounded-lg hover:bg-white/30 transition">
                      {copiedCode === coupon.code ? <CheckCircle size={16} /> : <Copy size={16} />}
                    </button>
                  </div>

                  {/* Dashed line */}
                  <div className="border-t border-dashed border-white/30 mt-4 pt-3">
                    <p className="text-white text-xl font-bold">
                      {coupon.type === 'percentage' ? `${coupon.value}% OFF` : `Rs. ${coupon.value} OFF`}
                    </p>
                  </div>
                </div>

                {/* Bottom - Details */}
                <div className="p-4 space-y-2">
                  {coupon.description && (
                    <p className="text-xs text-gray-600 italic">"{coupon.description}"</p>
                  )}

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {coupon.minOrder > 0 && (
                      <div className="flex items-center gap-1 text-gray-600">
                        <DollarSign size={12} /> Min: Rs. {coupon.minOrder}
                      </div>
                    )}
                    {coupon.maxDiscount > 0 && (
                      <div className="flex items-center gap-1 text-gray-600">
                        <Percent size={12} /> Max: Rs. {coupon.maxDiscount}
                      </div>
                    )}
                    {coupon.usageLimit > 0 && (
                      <div className="flex items-center gap-1 text-gray-600">
                        <Users size={12} /> {coupon.usedCount || 0}/{coupon.usageLimit}
                      </div>
                    )}
                    {coupon.expiryDate && (
                      <div className="flex items-center gap-1 text-gray-600">
                        <Calendar size={12} /> {coupon.expiryDate}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                    <button onClick={() => openEditModal(coupon)}
                      className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-1">
                      <Edit size={14} /> Edit
                    </button>
                    <button onClick={() => handleDelete(coupon)}
                      className="flex-1 bg-red-50 hover:bg-red-100 text-red-700 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-1">
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[95vh] overflow-y-auto">

            <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10">
              <h2 className="text-lg font-bold text-gray-800">
                {editingCoupon ? 'Edit Coupon' : 'Add Coupon'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:bg-gray-100 p-2 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-5 space-y-4">

              {message.text && (
                <div className={`text-sm p-3 rounded-lg flex items-center gap-2 ${
                  message.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  <AlertCircle size={14} />
                  {message.text}
                </div>
              )}

              {/* Code */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Coupon Code *</label>
                <div className="flex gap-2">
                  <input type="text" value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className="flex-1 border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm font-mono uppercase"
                    placeholder="e.g., SAVE50" required />
                  <button type="button" onClick={generateCode}
                    className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-semibold">
                    Generate
                  </button>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Description (Optional)</label>
                <input type="text" value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                  placeholder="e.g., Summer Sale 50% Off" />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Discount Type *</label>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button"
                    onClick={() => setFormData({ ...formData, type: 'percentage' })}
                    className={`p-3 rounded-lg border-2 font-semibold text-sm flex items-center justify-center gap-2 ${
                      formData.type === 'percentage'
                        ? 'border-green-600 bg-green-50 text-green-800'
                        : 'border-gray-200 text-gray-600'
                    }`}>
                    <Percent size={14} /> Percentage
                  </button>
                  <button type="button"
                    onClick={() => setFormData({ ...formData, type: 'flat' })}
                    className={`p-3 rounded-lg border-2 font-semibold text-sm flex items-center justify-center gap-2 ${
                      formData.type === 'flat'
                        ? 'border-green-600 bg-green-50 text-green-800'
                        : 'border-gray-200 text-gray-600'
                    }`}>
                    <DollarSign size={14} /> Flat (Rs.)
                  </button>
                </div>
              </div>

              {/* Value */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Discount Value * {formData.type === 'percentage' ? '(%)' : '(Rs.)'}
                </label>
                <input type="number" value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                  placeholder={formData.type === 'percentage' ? 'e.g., 10 (for 10%)' : 'e.g., 100 (for Rs. 100 off)'}
                  min="0" required />
              </div>

              {/* Min Order + Max Discount */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Min Order (Rs.)</label>
                  <input type="number" value={formData.minOrder}
                    onChange={(e) => setFormData({ ...formData, minOrder: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                    placeholder="e.g., 500" min="0" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Max Discount (Rs.)</label>
                  <input type="number" value={formData.maxDiscount}
                    onChange={(e) => setFormData({ ...formData, maxDiscount: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                    placeholder="e.g., 500" min="0" />
                  <p className="text-[10px] text-gray-500 mt-1">Percentage coupons ke liye cap</p>
                </div>
              </div>

              {/* Usage Limit + Expiry */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Usage Limit</label>
                  <input type="number" value={formData.usageLimit}
                    onChange={(e) => setFormData({ ...formData, usageLimit: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                    placeholder="e.g., 100 (0 = unlimited)" min="0" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Expiry Date</label>
                  <input type="date" value={formData.expiryDate}
                    onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm" />
                </div>
              </div>

              {/* Active */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-5 h-5 text-green-700" />
                <span className="text-sm font-semibold text-gray-700">Active (customers use kar sakte hain)</span>
              </label>

              <div className="flex gap-3 pt-2 border-t border-gray-200">
                <button type="button" onClick={() => setShowModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition">
                  Cancel
                </button>
                <button type="submit"
                  className="flex-1 bg-green-700 text-white py-3 rounded-lg font-semibold hover:bg-green-800 transition flex items-center justify-center gap-2">
                  <Save size={18} /> {editingCoupon ? 'Update' : 'Create'}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Success!"
        message={successMessage}
        buttonText="Great!"
      />

    </div>
  );
};

export default CouponManagement;